import React, { useState, useEffect } from "react";
import {
  getDeviceIcon,
  getDeviceLabel,
  loadDeviceIconSvg,
} from "../../../utils/deviceIcons";
import Tooltip from "../../shared-modules/Tooltip";
import styles from "./DeviceIconList.module.css";

const DeviceIconList = ({ devices = [], size = "small" }) => {
  const [svgContents, setSvgContents] = useState({});

  const getColor = (colorValue) => {
    if (!colorValue) return "#6B7280";

    const colorMap = {
      red: "#EF4444",
      green: "#10B981",
      yellow: "#F59E0B",
      "#EF4444": "#EF4444",
      "#10B981": "#10B981",
      "#F59E0B": "#F59E0B",
    };

    return colorMap[colorValue.toLowerCase()] || colorValue;
  };

  useEffect(() => {
    let active = true;
    const loadSvgs = async () => {
      const uniqueIcons = new Set(
        devices.map((device) => getDeviceIcon(device.device_name))
      );

      const contents = {};
      // Icons are fetched once per session via the shared module cache in
      // deviceIcons.js (DeviceIconList mounts once per patient row, so a raw
      // per-instance fetch would re-download the same ~9 icons on every row).
      await Promise.all(
        Array.from(uniqueIcons).map(async (iconName) => {
          contents[iconName] = await loadDeviceIconSvg(iconName);
        })
      );
      if (active) setSvgContents(contents);
    };

    if (devices.length > 0) {
      loadSvgs();
    }

    return () => {
      active = false;
    };
  }, [devices]);

  if (!devices || devices.length === 0) {
    return null;
  }

  const getDeviceTooltipContent = (device) => {
    const label = getDeviceLabel(device.device_name);
    let content = `<strong>${label}</strong>`;

    if (device.connectivity_status) {
      const statusLabels = {
        connected: "Connected",
        inactive: "Inactive",
        not_connected: "Not Connected",
      };
      const statusLabel = statusLabels[device.connectivity_status] || device.connectivity_status;
      content += `<br/><span style="opacity: 0.8;">Status: ${statusLabel}</span>`;
    }

    if (device.connectivity_reason) {
      content += `<br/><span style="opacity: 0.8; font-size: 11px;">${device.connectivity_reason}</span>`;
    }

    return content;
  };

  return (
    <div className={`${styles.deviceIconList} ${styles[`size-${size}`]}`}>
      {devices.map((device, index) => {
        const iconName = getDeviceIcon(device.device_name);
        const svgContent = svgContents[iconName];
        const color = getColor(device.connectivity_color);

        if (!svgContent) {
          return null;
        }

        let coloredSvg = svgContent
          // Replace currentColor
          .replace(/currentColor/g, color)
          // Replace stroke attributes (skip "none")
          .replace(/stroke="(?!none)([^"]+)"/g, `stroke="${color}"`)
          // Replace fill attributes (skip "none", "white", "#fff", "#ffffff")
          .replace(/fill="(?!none|white|#fff|#ffffff)([^"]+)"/g, `fill="${color}"`)
          // Replace inline style stroke
          .replace(/stroke:\s*(?!none)[^;"}]+/g, `stroke: ${color}`)
          // Replace inline style fill (skip none/white)
          .replace(/fill:\s*(?!none|white|#fff|#ffffff)[^;"}]+/g, `fill: ${color}`)
          // Normalize stroke-width for consistent outline weight
          .replace(/stroke-width="[^"]+"/g, `stroke-width="1.5"`)
          // Normalize stroke-width in inline styles too
          .replace(/stroke-width:\s*[^;"}]+/g, `stroke-width: 1.5`)
          // Set size on SVG element
          .replace(
            /<svg([^>]*)>/,
            `<svg$1 style="color: ${color}; width: 14.4px; height: 14.4px;">`
          );

        return (
          <Tooltip
            key={device.device_id || index}
            content={
              <div
                dangerouslySetInnerHTML={{ __html: getDeviceTooltipContent(device) }}
                style={{ whiteSpace: "nowrap" }}
              />
            }
            position="right"
          >
            <span
              className={styles.deviceIcon}
              dangerouslySetInnerHTML={{ __html: coloredSvg }}
              aria-label={getDeviceLabel(device.device_name)}
            />
          </Tooltip>
        );
      })}
    </div>
  );
};

export default DeviceIconList;